import type { Locale } from 'date-fns'
import type { ComposerTranslation } from 'vue-i18n'
import { TZDate } from '@date-fns/tz'
import { addDays, addHours, addMinutes, differenceInMinutes, endOfDay, endOfMonth, endOfYear, format as formatDateFns, formatDistance as formatDistanceDateFns, formatDuration as formatDurationDateFns, formatISO, intervalToDuration, isBefore, isSameDay, isValid, parse, parseISO, startOfDay, startOfMonth, startOfYear } from 'date-fns'
import { de as deLocale } from 'date-fns/locale/de'
import { cs as csLocale } from 'date-fns/locale/cs'
import { fr as frLocale } from 'date-fns/locale/fr'
import { es as esLocale } from 'date-fns/locale/es'
import { it as itLocale } from 'date-fns/locale/it'
import { enGB as enLocale } from 'date-fns/locale/en-GB'
import i18n from '@/plugins/i18n'
import { WALL_CLOCK_MODEL, WAYPOINT_DATE_DAY_FORMAT, WAYPOINT_DATE_FORMAT } from '@/utils/const'
import { LanguageEnum } from '@apiTypes'

/** Calendar date as ISO-8601 (`2026-09-14`), with no time. */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd'

export function stringToDate(dateString: string | undefined | null): Date | undefined {
  return dateString ? parseISO(dateString) : undefined
}

export function isDateInThePast(dateString: string | null | undefined): boolean {
  const parsedDate = stringToDate(dateString)

  return !!parsedDate && isBefore(parsedDate, new Date())
}

/**
 * Whether an already-resolved instant has passed.
 *
 * The sibling above parses its input, which reads a naive wall clock on the reader's clock. Callers
 * holding a wall clock at some other zone must resolve it themselves - via `wallClockToInstant` -
 * and pass the instant here.
 */
export function isInstantInThePast(instant: Date | undefined): boolean {
  return !!instant && isBefore(instant, new Date())
}

export function stringsToDate(dateStrings: string | string[]): Date[] {
  if (Array.isArray(dateStrings))
    return dateStrings.map(string => stringToDate(string)).filter((date): date is Date => !!date)

  return [stringToDate(dateStrings)].filter((date): date is Date => !!date)
}

export function formatDateRange(from: string | undefined | null, to: string | undefined | null, prefix = '', includeHours = true): string {
  return formatDateRangeFromDates(stringToDate(from), stringToDate(to), prefix, includeHours)
}

/** The wall clock at `timeZone` for the same instant; a null zone keeps the reader's browser. */
export function inZone(date: Date | undefined, timeZone: string | null | undefined): Date | undefined {
  if (!date || !timeZone)
    return date

  try {
    // TZDate yields an Invalid Date rather than throwing, so the result has to be checked, not just
    // the call guarded. The instant is real either way, and a blank cell reads as missing data.
    const zoned = new TZDate(date, timeZone)

    return isValid(zoned) ? zoned : date
  }
  catch {
    return date
  }
}

export function formatDateInZone(date: Date | undefined, timeZone: string | null | undefined, dateFormat = 'dd.MM.yyyy HH:mm'): string {
  return formatDate(inZone(date, timeZone), dateFormat)
}

export function startOfCalendarDayInZone(
  timeZone: string | null | undefined,
  now: Date = new Date(),
): Date {
  if (!timeZone)
    return startOfDay(now)

  const key = formatDateInZone(now, timeZone, ISO_DATE_FORMAT)

  return stringToDate(key) ?? startOfDay(now)
}

/** The next 00:00 at `timeZone` after `from` (reader clock when the zone is missing). */
export function nextCalendarMidnightInZone(
  timeZone: string | null | undefined,
  from: Date = new Date(),
): Date {
  if (!timeZone)
    return startOfDay(addDays(from, 1))

  try {
    const todayKey = formatDateInZone(from, timeZone, ISO_DATE_FORMAT)
    const [year, month, day] = todayKey.split('-').map(Number)
    const nextUtc = new Date(Date.UTC(year, month - 1, day + 1))

    const zoned = new TZDate(
      nextUtc.getUTCFullYear(),
      nextUtc.getUTCMonth(),
      nextUtc.getUTCDate(),
      0,
      0,
      timeZone,
    )

    return isValid(zoned) ? zoned : startOfDay(addDays(from, 1))
  }
  catch {
    return startOfDay(addDays(from, 1))
  }
}

export function formatDateStringInZone(date: string | null | undefined, timeZone: string | null | undefined, dateFormat = 'dd.MM.yyyy HH:mm'): string {
  return formatDateInZone(stringToDate(date), timeZone, dateFormat)
}

export function formatDateRangeInZone(from: string | undefined | null, to: string | undefined | null, timeZone: string | null | undefined, prefix = '', includeHours = true): string {
  return formatDateRangeFromDates(inZone(stringToDate(from), timeZone), inZone(stringToDate(to), timeZone), prefix, includeHours)
}

/**
 * Does this string end in a UTC offset - an instant rather than a wall clock? The time part is
 * required, or a bare `2026-09-15` matches on its own `-15`.
 */
export const OFFSET_SUFFIX = /\d{2}:\d{2}[\d:.]*(?:Z|[+-]\d{2}(?::?\d{2})?)$/i

/**
 * An instant as the wall clock at `timeZone`, for a naive-model picker to display.
 *
 * A value with no offset is already a wall clock and is not re-zoned, mirroring the guard in
 * `wallClockToInstant` - both directions have to be idempotent or a value that crosses twice is
 * corrupted. It is still re-emitted through the model format, which the picker needs.
 */
export function instantToWallClock(iso: string | null | undefined, timeZone: string | null | undefined): string {
  const value = iso?.trim()

  if (value && !OFFSET_SUFFIX.test(value))
    return formatWallClock(stringToDate(value)) || value

  return instantDateToWallClock(stringToDate(iso), timeZone)
}

/**
 * Read a naive wall clock at `timeZone` and return the instant it names. A value that already
 * carries an offset is a real instant and is left alone.
 */
export function wallClockToInstant(value: string | null | undefined, timeZone: string | null | undefined): Date | undefined {
  const parsed = stringToDate(value)
  if (!parsed || !timeZone || !value || OFFSET_SUFFIX.test(value.trim()))
    return parsed

  try {
    return new TZDate(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), parsed.getHours(), parsed.getMinutes(), timeZone)
  }
  catch {
    return parsed
  }
}

/** A local Date's digits, with no offset - the shape the waypoint API expects. */
export function formatWallClock(date: Date | undefined): string {
  return formatDate(date, WALL_CLOCK_MODEL)
}

/** The wall clock at `timeZone` for an instant already parsed into a `Date`. */
export function instantDateToWallClock(date: Date | undefined, timeZone: string | null | undefined): string {
  return formatDateInZone(date, timeZone, WALL_CLOCK_MODEL)
}

function formatDateRangeFromDates(fromDate: Date | undefined, toDate: Date | undefined, prefix = '', includeHours = true): string {
  const format = includeHours ? WAYPOINT_DATE_FORMAT : WAYPOINT_DATE_DAY_FORMAT
  if (fromDate && toDate) {
    const fromDateFormatted = formatDate(fromDate, format)
    const toDateFormatted = formatDate(toDate, format)

    // See format_range: with hours shown, equal text is not enough to collapse, because both ends of
    // an autumn fold render as the same wall clock. Without hours the text is just a date.
    if (fromDateFormatted === toDateFormatted && (!includeHours || fromDate.getTime() === toDate.getTime()))
      return prefix ? `${prefix} ${fromDateFormatted}` : fromDateFormatted
    else if (isSameDay(fromDate, toDate)) {
      if (includeHours)
        return `${fromDateFormatted} - ${formatDate(toDate, 'HH:mm')}`

      return fromDateFormatted
    }

    return `${fromDateFormatted} - ${toDateFormatted}`
  }

  const formatted = formatDate(fromDate || toDate, format)

  return prefix && formatted ? `${prefix} ${formatted}` : formatted
}

export function formatCurrentDateMonth(): string {
  const currentDate = new Date()
  const startOfTheMonth = startOfMonth(currentDate)
  const dateFormat = 'd. MMM'

  return `${formatDate(startOfTheMonth, dateFormat)} - ${formatDate(currentDate, dateFormat)}`
}

export function formatDateWithSecondsString(date: string | null | undefined): string {
  return formatDateString(date, 'dd.MM.yyyy HH:mm:ss')
}

export function formatDateString(date: string | null | undefined, dateFormat = 'dd.MM.yyyy HH:mm'): string {
  const parsedDate = stringToDate(date)

  return formatDate(parsedDate, dateFormat)
}

/**
 * Relative day label: "Today" for the current day, otherwise "dd.MM.yyyy".
 */
export function formatRelativeDayLabel(date: Date, now: Date = new Date()): string {
  return isSameDay(date, now) ? i18n.global.t('Today') : formatDate(date, 'dd.MM.yyyy')
}

/** `formatRelativeDayLabel` on the address clock: "Today" means today *there*. */
export function formatRelativeDayLabelInZone(date: Date, timeZone: string | undefined, now: Date = new Date()): string {
  return formatDateInZone(date, timeZone, ISO_DATE_FORMAT) === formatDateInZone(now, timeZone, ISO_DATE_FORMAT)
    ? i18n.global.t('Today')
    : formatDateInZone(date, timeZone, 'dd.MM.yyyy')
}

/** "CET (GMT+1)" in the UI language, "GMT+9" when the zone has no abbreviation, "" for a blank or unknown zone. */
export function formatTimeZoneLabel(timeZone: string, at: Date = new Date(), language: string = i18n.global.locale.value): string {
  if (!timeZone || !isValid(at))
    return ''

  try {
    // en-US has no CET/CEST
    const locale = language === 'en' ? 'en-GB' : language

    const timeZonePart = (timeZoneName: 'short' | 'shortOffset') => new Intl.DateTimeFormat(locale, { timeZone, timeZoneName })
      .formatToParts(at)
      .find(part => part.type === 'timeZoneName')?.value ?? ''

    const abbreviation = timeZonePart('short')
    const offset = timeZonePart('shortOffset')

    return /^(GMT|UTC)/.test(abbreviation) ? offset : `${abbreviation} (${offset})`
  }
  catch {
    return ''
  }
}

/**
 * Bidding-history timestamp: "Today, 15:32" for today, otherwise "dd.MM.yyyy HH:mm".
 */
export function formatBidHistoryTime(date: string | null | undefined): string {
  const parsedDate = stringToDate(date)
  if (!parsedDate)
    return ''

  // Read once so the label and the separator cannot disagree across midnight.
  const now = new Date()
  const separator = isSameDay(parsedDate, now) ? ', ' : ' '

  return `${formatRelativeDayLabel(parsedDate, now)}${separator}${formatDate(parsedDate, 'HH:mm')}`
}

export function reformatDateString(date: string | null | undefined, fromFormat: string, toFormat?: string): string | null | undefined {
  if (!date)
    return date

  const parsedDate = parse(date, fromFormat, new Date(1970, 1, 1))

  return toFormat ? formatDate(parsedDate, toFormat) : formatISODate(parsedDate)
}

export function formatDate(date: Date | undefined, dateFormat = 'dd.MM.yyyy HH:mm:ss'): string {
  return date && isValid(date) ? formatDateFns(date, dateFormat) : ''
}

export function formatISODate(date: Date | undefined): string {
  return date && isValid(date) ? formatISO(date) : ''
}

export function splitDateTime(dateString: string | null | undefined, timeZone?: string): { date: string; time: string } {
  const date = stringToDate(dateString)

  return { date: formatDateInZone(date, timeZone, 'dd.MM.yyyy'), time: formatDateInZone(date, timeZone, 'HH:mm') }
}

export function durationInMinutes(start: string | null | undefined, end: string | null | undefined): number | null {
  if (!start || !end)
    return null

  const startDate = parseISO(start)
  const endDate = parseISO(end)
  if (!isValid(startDate) || !isValid(endDate))
    return null

  return differenceInMinutes(endDate, startDate)
}

export function formatDurationInMinutes(t: ComposerTranslation, start: string | null | undefined, end: string | null | undefined, emptyValue = '-'): string {
  const minutes = durationInMinutes(start, end)
  if (minutes === null)
    return emptyValue

  return t('No duration | { count } minute | { count } minutes | { count } minutes', { count: minutes })
}

export function calculateDuration(days: number, hours: number, minutes: number): string {
  let totalMinutes = days * 24 * 60 + hours * 60 + minutes
  const totalDays = Math.floor(totalMinutes / (60 * 24))

  totalMinutes -= totalDays * 60 * 24

  const totalHours = Math.floor(totalMinutes / 60)

  totalMinutes -= totalHours * 60

  const time = [totalHours, totalMinutes, 0].map(part => part.toString().padStart(2, '0')).join(':')

  if (totalDays > 0)
    return `${totalDays} ${time}`
  else
    return time
}

export function parseDuration(value: string | null | undefined): { days: number; hours: number; minutes: number } {
  let parts = (value || '').split(' ', 2)
  let days = 0
  let timePart: string
  if (parts.length > 1) {
    days = +parts[0].trim()
    timePart = parts[1]
  }
  else {
    timePart = parts[0]
  }

  parts = timePart.split(':', 3)

  return { days, hours: +parts[0], minutes: +parts[1] }
}

export function formatRelativeDuration(dateString: string | null | undefined, duration: string | null | undefined, currentDate: Date, langauge: LanguageEnum): string {
  const relativeDate = parseRelativeDate(dateString, duration)

  return relativeDate ? formatDistanceDateFns(relativeDate, currentDate, { locale: getDateFnsLocale(langauge), addSuffix: true }) : ''
}

export function parseRelativeDate(dateString: string | null | undefined, duration: string | null | undefined): Date | undefined {
  let relativeDate = stringToDate(dateString)
  if (relativeDate) {
    const { days, hours, minutes } = parseDuration(duration)

    relativeDate = addDays(relativeDate, days)
    relativeDate = addHours(relativeDate, hours)

    return addMinutes(relativeDate, minutes)
  }

  return undefined
}

export function durationToSeconds(duration: string | null | undefined): number {
  const { days, hours, minutes } = parseDuration(duration)

  return days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60
}

export function formatDurationDate(dateString: string | null | undefined, duration: string | null | undefined): string {
  const relativeDate = parseRelativeDate(dateString, duration)

  return relativeDate ? formatDate(relativeDate) : ''
}

export function getDateFnsLocale(language: LanguageEnum | string): Locale {
  switch (language) {
    case LanguageEnum.De:
      return deLocale
    case LanguageEnum.Cs:
      return csLocale
    case LanguageEnum.Fr:
      return frLocale
    case LanguageEnum.Es:
      return esLocale
    case LanguageEnum.It:
      return itLocale
    default:
      return enLocale
  }
}

export function formatRelativeDate(dateString: string | null | undefined, fromDate: Date, langauge: LanguageEnum): string {
  const baseDate = stringToDate(dateString)

  return baseDate ? formatDistanceDateFns(baseDate, fromDate, { locale: getDateFnsLocale(langauge), addSuffix: true }) : ''
}

export function formatDuration(dateString: string | null | undefined, langauge: LanguageEnum): string {
  return formatDurationDateFns({
    ...parseDuration(dateString),
  }, { locale: getDateFnsLocale(langauge) })
}

export function formatDurationShort(start: Date, end: Date, langauge: LanguageEnum): string {
  const duration = intervalToDuration({
    start: start < end ? start : end,
    end: start < end ? end : start,
  })

  // we do not want to show seconds
  duration.seconds = 0

  const delimiter = ', '

  // Get full formatted duration (e.g., "3 days, 2 hours, 4 minutes")
  const fullText = formatDurationDateFns(duration, { delimiter, locale: getDateFnsLocale(langauge) })

  return fullText
    .split(delimiter)
    .map(part => {
      const [num, unit] = part.split(' ') // Extract number & unit (e.g., ["3", "days"])

      return unit ? `${num}${unit.charAt(0)}` : '' // Take the first letter (e.g., "d" from "days")
    })
    .join(' ') // Join as "3d 2h 4m"
}

export function formatCountdownDuration(dateString: string | null | undefined, durationString: string | null | undefined, currentDate: Date, langauge: LanguageEnum): string {
  const relativeDate = parseRelativeDate(dateString, durationString)
  if (relativeDate) {
    const { t } = i18n.global
    const seconds = Math.max(0, Math.ceil((relativeDate.getTime() - currentDate.getTime()) / 1000))
    if (seconds <= 180)
      return t('Less than 3 minutes remaining')

    const duration = intervalToDuration({
      start: currentDate,
      end: relativeDate,
    })

    // we do not want to show seconds
    duration.seconds = 0

    const prefix = t('Ends in')
    const durationFormatted = formatDurationDateFns(duration, { delimiter: ' ', locale: getDateFnsLocale(langauge) })

    return `${prefix} ${durationFormatted}`
  }

  return ''
}

export function formatSimpleCountdownDuration(dateString: string | null | undefined, currentDate: Date): string {
  const parsedDate = stringToDate(dateString)
  if (parsedDate) {
    let seconds = Math.max(0, Math.ceil((parsedDate.getTime() - currentDate.getTime()) / 1000))
    const minutes = Math.floor(seconds / 60)

    seconds = seconds - (minutes * 60)

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  return ''
}

export function isWholeMonthRange(datesString: string | string[], month: number, year: number): boolean {
  const dates = stringsToDate(datesString)
  if (dates.length !== 2)
    return false

  if (dates.some(date => date.getMonth() !== month || date.getFullYear() !== year))
    return false

  return startOfMonth(dates[0]).getTime() === dates[0].getTime() && endOfMonth(dates[1]).getTime() === endOfDay(dates[1]).getTime()
}

export function isWholeYearRange(datesString: string | string[], year: number): boolean {
  const dates = stringsToDate(datesString)
  if (dates.length !== 2)
    return false

  if (dates.some(date => date.getFullYear() !== year))
    return false

  return startOfYear(dates[0]).getTime() === dates[0].getTime() && endOfYear(dates[1]).getTime() === endOfDay(dates[1]).getTime()
}
